/** 界面：首次设置、配对、顶栏（含背景音乐）、照片面板（含编辑回忆、删除）、提示、邀请、星海设置、回收站 */
import { LIMITS, formatCode, localDateOf, normalizeCode, parseLocalDate, type CapsuleDto, type CapsuleKind, type InviteKind, type InviteResponse, type LocalDate, type NoteDto, type Shine, type TrashedPhotoDto } from '@star-sea/shared';
import type { CapsulePhase } from './capsule.ts';
import { el, field, input } from './dom.ts';
import type { FieldName, MemoryFields } from './edit.ts';
import { writtenOn, type CardFace, type CardFaces } from './flip.ts';
import { tickBuzz } from './haptics.ts';
import { wrapLines } from './words.ts';
import { PhotoMorph } from './morph.ts';
import type { DustImage, Point, Stardust } from './stardust.ts';

export function mountShell(root: HTMLElement) {
  const stage = el('div', { class: 'stage' });
  const labels = el('div', { class: 'labels', attrs: { 'aria-hidden': 'true' } });
  const overlay = el('div', { class: 'overlay' });
  // 盖在面板上面的特效层（星尘），不接收点击
  const fx = el('div', { class: 'fx', attrs: { 'aria-hidden': 'true' } });
  const toasts = el('div', { class: 'toasts', attrs: { role: 'status', 'aria-live': 'polite' } });
  root.replaceChildren(stage, labels, overlay, fx, toasts);
  return { stage, labels, overlay, fx, toasts };
}

// ── 全屏卡片：连接中、出错、首次设置、配对 ───────────────────────────

function screen(...children: HTMLElement[]): HTMLElement {
  return el('div', { class: 'screen' }, el('div', { class: 'card' }, ...children));
}

export function loadingScreen(text = '正在连接星海…'): HTMLElement {
  return screen(el('p', { class: 'card-quiet', text }));
}

export function errorScreen(title: string, detail: string, retry: () => void): HTMLElement {
  return screen(
    el('h1', { class: 'card-title', text: title }),
    el('p', { class: 'card-text', text: detail }),
    el('button', { class: 'btn btn-primary', text: '重试', on: { click: retry } }),
  );
}

function formError(): HTMLParagraphElement {
  return el('p', { class: 'form-error', attrs: { role: 'alert' } });
}

async function submitting(form: HTMLFormElement, err: HTMLElement, work: () => Promise<void>): Promise<void> {
  const btn = form.querySelector<HTMLButtonElement>('button[type=submit]');
  err.textContent = '';
  if (btn) btn.disabled = true;
  try {
    await work();
  } catch (e) {
    err.textContent = e instanceof Error ? e.message : String(e);
  } finally {
    if (btn) btn.disabled = false;
  }
}

export interface SetupValues {
  code: string;
  spaceName: string;
  since: LocalDate;
  displayName: string;
}

export function setupScreen(onSubmit: (v: SetupValues) => Promise<void>): HTMLElement {
  const code = input({ name: 'code', required: '', autocomplete: 'off', autocapitalize: 'characters', spellcheck: 'false', placeholder: 'XXXX-XXXX' });
  const name = input({ name: 'spaceName', required: '', maxlength: '32', value: '我们的星海' });
  const since = input({ name: 'since', type: 'date', required: '', max: localDateOf(new Date()) });
  const me = input({ name: 'displayName', required: '', maxlength: '24', placeholder: '比如：阿禾', autocomplete: 'nickname' });
  const err = formError();
  const form = el(
    'form',
    { class: 'form' },
    el('h1', { class: 'card-title', text: '建一片星海' }),
    el('p', { class: 'card-text', text: '每一颗星都是你们的一张照片。先建好星海，再邀请 Ta 加入。' }),
    field('设置码', code, '星海服务启动时打印在终端里，只能用一次'),
    field('星海的名字', name),
    field('在一起的日子', since, '螺线从这一天开始，每过一年向外绕一圈'),
    field('你的称呼', me),
    err,
    el('button', { class: 'btn btn-primary btn-block', text: '建好星海', attrs: { type: 'submit' } }),
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void submitting(form, err, () => onSubmit({ code: normalizeCode(code.value), spaceName: name.value.trim(), since: since.value as LocalDate, displayName: me.value.trim() }));
  });
  return screen(form);
}

/** 是不是从主屏幕（PWA）打开的；iOS 用 navigator.standalone */
function standalone(): boolean {
  return matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export interface PairValues {
  code: string;
  displayName: string;
}

export function pairScreen(prefill: string, onSubmit: (v: PairValues) => Promise<void>): HTMLElement {
  const code = input({ name: 'code', required: '', autocomplete: 'off', autocapitalize: 'characters', spellcheck: 'false', placeholder: 'XXXXX-XXXXX', value: prefill ? formatCode(prefill) : '' });
  const me = input({ name: 'displayName', maxlength: '24', placeholder: '比如：小满', autocomplete: 'nickname' });
  const err = formError();
  const form = el(
    'form',
    { class: 'form' },
    el('h1', { class: 'card-title', text: '加入星海' }),
    el('p', { class: 'card-text', text: '输入邀请码，这台设备就能看、也能添照片。' }),
    field('邀请码', code, '邀请链接里自带；24 小时内有效，只能用一次'),
    field('你的称呼', me, '给自己加另一台设备时可以不填'),
    err,
    el('button', { class: 'btn btn-primary btn-block', text: '加入星海', attrs: { type: 'submit' } }),
    el('p', { class: 'card-quiet', text: '还没有邀请码？请已经在星海里的那个人点右上角的「邀请」。' }),
    // iPhone 上从主屏幕打开的星海和 Safari 不共用存储，Safari 里的登录带不过来，要在这里再输一次邀请码
    standalone() ? el('p', { class: 'card-quiet', text: '从主屏幕打开的星海要单独加入一次：在已经加入的设备上点「邀请 → 加我的另一台设备」，把邀请码输到这里。' }) : null,
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void submitting(form, err, () => onSubmit({ code: normalizeCode(code.value), displayName: me.value.trim() }));
  });
  if (!prefill) queueMicrotask(() => code.focus());
  return screen(form);
}

// ── 入场 ─────────────────────────────────────────────────────────

/** 入场时压在爱心下方的文字和按钮；任意位置的点击都算「轻触」，只有「跳过」按钮算跳过 */
export function mountIntro(parent: HTMLElement, opts: { title: string; sub: string }) {
  const tap = el('button', { class: 'intro-tap', text: '轻触心跳 · 点亮星海', attrs: { type: 'button' } });
  const skip = el('button', { class: 'intro-skip', text: '跳过', attrs: { type: 'button' } });
  const root = el(
    'div',
    { class: 'intro' },
    el('div', { class: 'intro-in' }, el('p', { class: 'intro-title', text: opts.title }), el('p', { class: 'intro-sub', text: opts.sub }), tap),
    skip,
  );
  parent.append(root);
  queueMicrotask(() => tap.focus({ preventScroll: true }));
  return {
    skip,
    pulse(beat: number) {
      tap.style.transform = `scale(${(1 + beat * 0.05).toFixed(3)})`;
    },
    triggered() {
      tap.hidden = true;
      skip.hidden = true;
    },
    leave() {
      root.classList.add('intro-done');
    },
    remove() {
      // 等淡出动画结束再拿掉
      setTimeout(() => root.remove(), 1300);
    },
  };
}

/** 和标题一样的宋体：画布里不能用 CSS 变量，照抄 style.css 的 --serif */
const WORDS_FONT = `'Songti SC', 'Noto Serif SC', 'Source Han Serif SC', 'STSong', serif`;

/**
 * 入场爱心炸开以后的那段话（节奏在 intro.ts）。字画在一块方形的透明画布上：这块画布就是之后化成星尘的那张图
 * （stardust.ts 的 wipe，方的格点横竖一样密），交接时字不会跳。后面一层很淡的暗晕，压在亮的中心光晕上也看得清。
 * by 是下面一行小字（Ta 留的入场暗号：「来自 阿禾 · 昨夜留」），和那段话画在同一块画布上、一起化成星尘；
 * onShow 在字浮现的那一刻调（入场暗号这时算 Ta 看到了）。
 */
export function mountIntroWords(parent: HTMLElement, o: { text: string; by?: string; dust: Stardust; reduced: boolean; onShow?: () => void }) {
  const veil = el('div', { class: 'intro-veil' });
  const canvas = el('canvas', { class: 'intro-words' });
  const spoken = o.by ? `${o.text}\n${o.by}` : o.text;
  const root = el('div', { class: 'intro-words-wrap', attrs: { role: 'status' } }, veil, canvas, el('span', { class: 'visually-hidden', text: spoken }));
  parent.append(root);
  let drawn = false;
  const draw = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const size = Math.round(Math.min(34, Math.max(20, innerWidth * 0.055)));
    const spacing = size * 0.14, lineH = size * 1.75, font = `500 ${size}px ${WORDS_FONT}`;
    const g = canvas.getContext('2d');
    if (!g) return;
    g.font = font;
    // 一个字一个字画，字距自己加（Safari 的 canvas 不一定支持 letterSpacing）
    const width = (s: string) => Math.max(0, [...s].reduce((x, ch) => x + g.measureText(ch).width + spacing, 0) - spacing);
    const lines = wrapLines(o.text, width, Math.min(innerWidth - 48, 640));
    // 小字那一行：字号是正文的一半多一点，和正文隔开半行
    const bySize = Math.round(size * 0.56), byFont = `400 ${bySize}px ${WORDS_FONT}`, byGap = o.by ? size * 0.55 : 0;
    g.font = byFont;
    const byW = o.by ? g.measureText(o.by).width : 0;
    g.font = font;
    const textW = Math.max(byW, ...lines.map(width)), textH = lines.length * lineH + byGap + (o.by ? bySize : 0), pad = size * 1.2;
    const side = Math.ceil(Math.max(textW, textH) + pad * 2);
    // 改尺寸会清掉画布的状态，字体、变换都要重新设
    canvas.width = canvas.height = Math.round(side * dpr);
    canvas.style.width = canvas.style.height = `${side}px`;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.font = font;
    g.textBaseline = 'middle';
    const y0 = side / 2 - textH / 2 + lineH / 2;
    // 第一遍带一圈玫瑰色的辉光，第二遍是清楚的字
    for (const glow of [true, false]) {
      g.shadowColor = glow ? 'rgba(255, 186, 210, 0.65)' : 'transparent';
      g.shadowBlur = glow ? size * 0.7 : 0;
      g.fillStyle = glow ? 'rgba(255, 236, 228, 0.85)' : '#fff6ec';
      lines.forEach((line, k) => {
        let x = side / 2 - width(line) / 2;
        for (const ch of line) {
          g.fillText(ch, x, y0 + k * lineH);
          x += g.measureText(ch).width + spacing;
        }
      });
      if (o.by) {
        g.font = byFont;
        g.textAlign = 'center';
        g.fillStyle = glow ? 'rgba(255, 214, 226, 0.5)' : 'rgba(255, 228, 214, 0.78)';
        g.fillText(o.by, side / 2, y0 + (lines.length - 1) * lineH + lineH / 2 + byGap + bySize / 2);
        g.textAlign = 'start';
        g.font = font;
      }
    }
    drawn = true;
  };
  return {
    show() {
      draw();
      root.classList.add('on');
      o.onShow?.();
    },
    /** 化成星尘飘走；减少动态效果、没有 WebGL 时淡出。返回要多久（秒） */
    wipe(): number {
      const ok = !o.reduced && drawn && o.dust.wipe({ image: canvas, accent: '#ffb3c8', rect: canvas.getBoundingClientRect() });
      // 星尘接手：画布上的字马上藏起来（同一张图已经在星尘那层烧着了）；做不了就淡出
      root.classList.add(ok ? 'gone' : 'fade');
      return ok ? 2.4 : 0.9;
    },
    remove() {
      root.remove();
    },
  };
}

/**
 * 日常入场（ritual.ts）：那段话在顶部浮现，停 hold 秒，再像散进星云一样隐去。
 * 不接收点击，星海照常转、照常点；减少动态效果时只淡入淡出。
 */
export function mountBanner(parent: HTMLElement, text: string, hold: number, by?: string): void {
  const node = el(
    'div',
    { class: 'intro-banner', attrs: { role: 'status' } },
    ...text.split('\n').map((line) => el('p', { text: line })),
    by ? el('p', { class: 'intro-banner-by', text: by }) : null,
  );
  parent.append(node);
  // 先让浏览器画一帧初始状态，过渡才会生效；用定时器不用 rAF（页面在后台时 rAF 不走）
  setTimeout(() => node.classList.add('on'), 30);
  setTimeout(() => node.classList.add('off'), 30 + (0.6 + hold) * 1000);
  setTimeout(() => node.remove(), 30 + (0.6 + hold + 1.8) * 1000);
}

// ── 顶栏 ─────────────────────────────────────────────────────────

export interface HudHandlers {
  onHome(): void;
  onFiles(files: File[]): void;
  onInvite(): void;
  onTrail(): void;
  /** 点星海的名字：改名字和在一起的日子 */
  onSettings(): void;
  /** 封存一颗时间胶囊 */
  onCapsule(): void;
  /** 给 Ta 留一句入场暗号 */
  onNote(): void;
}

/** 背景音乐的开关和换歌（sound.ts 的 Sound 就满足它） */
export interface MusicControls {
  readonly enabled: boolean;
  readonly name: string;
  readonly custom: boolean;
  setEnabled(on: boolean): void;
  useFile(file: File): Promise<void>;
  useDefault(): void;
}

function icon(parts: [string, Record<string, string>][]): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', 'icon');
  for (const [tag, attrs] of parts) {
    const node = document.createElementNS(ns, tag);
    for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
    svg.append(node);
  }
  return svg;
}

const musicIcon = () =>
  icon([
    ['path', { d: 'M9 17.5V6l10-2v11.5' }],
    ['circle', { cx: '6.5', cy: '17.5', r: '2.5' }],
    ['circle', { cx: '16.5', cy: '15.5', r: '2.5' }],
    // 关掉时显示的斜线
    ['path', { d: 'M4 4l16 16', class: 'icon-off' }],
  ]);

/** 星轨：一条连起几颗星的折线（同原型） */
const trailIcon = () =>
  icon([
    ['path', { d: 'M4 18l5-6 4 3 7-9' }],
    ['circle', { cx: '4', cy: '18', r: '1.4' }],
    ['circle', { cx: '9', cy: '12', r: '1.4' }],
    ['circle', { cx: '13', cy: '15', r: '1.4' }],
    ['circle', { cx: '20', cy: '6', r: '1.4' }],
  ]);

const pencilIcon = () =>
  icon([
    ['path', { d: 'M4 20h4L19 9l-4-4L4 16v4z' }],
    ['path', { d: 'M13.5 6.5l4 4' }],
  ]);

const trashIcon = () =>
  icon([
    ['path', { d: 'M4 7h16' }],
    ['path', { d: 'M9 7V4.5h6V7' }],
    ['path', { d: 'M6.5 7l1 12.5h9l1-12.5' }],
    ['path', { d: 'M10.5 11v5M13.5 11v5' }],
  ]);

/** 心动的一格：四角的星芒 */
const sparkIcon = () => icon([['path', { d: 'M12 3l2.1 6.9L21 12l-6.9 2.1L12 21l-2.1-6.9L3 12l6.9-2.1z' }]]);
/** 时间胶囊：一个沙漏 */
const capsuleIcon = () => icon([['path', { d: 'M7 3h10M7 21h10M8 3c0 5 8 5 8 9s-8 4-8 9M16 3c0 5-8 5-8 9s8 4 8 9' }]]);
/** 入场暗号：一个信封，角上一颗小星 */
const noteIcon = () =>
  icon([
    ['path', { d: 'M3.5 7.5h13v11h-13zM3.5 8l6.5 5 6.5-5' }],
    ['path', { d: 'M19.5 2.5v4M17.5 4.5h4' }],
  ]);

/** ♪ 按钮和它下面的小面板：开关、现在放的歌、换一首、恢复默认 */
/** 震动反馈的开关（haptics.ts）；只有能震的设备（安卓）才有 */
export interface HapticsControls {
  readonly enabled: boolean;
  setEnabled(on: boolean): void;
}

/**
 * 「给 Ta」：留一句入场暗号、封存一颗时间胶囊。手机上顶栏放不下两个图标（会折成两行、压住入场那段话），合成一个小菜单
 */
function forTaMenu(h: { onNote(): void; onCapsule(): void }): HTMLElement {
  const button = el('button', { class: 'btn btn-icon', attrs: { type: 'button', 'aria-label': '给 Ta', title: '给 Ta：入场暗号、时间胶囊', 'aria-haspopup': 'menu', 'aria-expanded': 'false' } }, noteIcon());
  const pop = el('div', { class: 'ta-pop', attrs: { role: 'menu', 'aria-label': '给 Ta' } });
  pop.hidden = true;
  const close = () => {
    pop.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    removeEventListener('pointerdown', outside, true);
    removeEventListener('keydown', onKey, true);
  };
  const outside = (e: PointerEvent) => {
    if (e.target instanceof Node && !pop.contains(e.target) && !button.contains(e.target)) close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    close();
    button.focus();
  };
  const item = (title: string, hint: string, glyph: SVGSVGElement, go: () => void) =>
    el(
      'button',
      {
        class: 'ta-item',
        attrs: { type: 'button', role: 'menuitem' },
        on: {
          click: () => {
            close();
            go();
          },
        },
      },
      glyph,
      el('span', { class: 'ta-item-text' }, el('span', { class: 'ta-item-title', text: title }), el('span', { class: 'ta-item-hint', text: hint })),
    );
  pop.append(item('留一句入场暗号', 'Ta 下次打开星海时看到', noteIcon(), h.onNote), item('封存一颗时间胶囊', '到日子才能拆开', capsuleIcon(), h.onCapsule));
  button.addEventListener('click', () => {
    if (!pop.hidden) return close();
    pop.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    addEventListener('pointerdown', outside, true);
    addEventListener('keydown', onKey, true);
    pop.querySelector<HTMLButtonElement>('.ta-item')?.focus();
  });
  return el('div', { class: 'hud-ta' }, button, pop);
}

function musicMenu(music: MusicControls, haptics: HapticsControls | null): HTMLElement {
  const button = el('button', { class: 'btn btn-icon', attrs: { type: 'button', 'aria-label': '背景音乐', title: '背景音乐', 'aria-haspopup': 'dialog', 'aria-expanded': 'false' } }, musicIcon());
  const toggle = input({ type: 'checkbox', role: 'switch' });
  const buzz = input({ type: 'checkbox', role: 'switch' });
  if (haptics) {
    buzz.checked = haptics.enabled;
    buzz.addEventListener('change', () => haptics.setEnabled(buzz.checked));
  }
  const name = el('p', { class: 'music-name' });
  const file = input({ type: 'file', accept: 'audio/*', hidden: '' });
  const reset = el('button', { class: 'btn btn-ghost', text: '恢复默认', attrs: { type: 'button' } });
  const status = el('p', { class: 'music-status', attrs: { role: 'status' } });
  const pop = el(
    'div',
    { class: 'music-pop', attrs: { role: 'dialog', 'aria-label': '背景音乐' } },
    el('label', { class: 'music-switch' }, toggle, el('span', { text: '背景音乐和入场音效' })),
    haptics ? el('label', { class: 'music-switch' }, buzz, el('span', { text: '震动反馈' })) : null,
    name,
    el('div', { class: 'music-row' }, el('label', { class: 'btn', text: '换一首…' }, file), reset),
    status,
    el('p', { class: 'music-note', text: '换的歌只在这次打开时有效，不会上传，也不存进浏览器。' }),
  );
  pop.hidden = true;
  const refresh = () => {
    button.setAttribute('aria-pressed', String(music.enabled));
    toggle.checked = music.enabled;
    name.textContent = `${music.enabled ? '正在放' : '已关闭'}：${music.name}`;
    reset.disabled = !music.custom;
  };
  const close = () => {
    pop.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    removeEventListener('pointerdown', outside, true);
    removeEventListener('keydown', onKey, true);
  };
  const outside = (e: PointerEvent) => {
    if (e.target instanceof Node && !pop.contains(e.target) && !button.contains(e.target)) close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    close();
    button.focus();
  };
  button.addEventListener('click', () => {
    if (!pop.hidden) return close();
    status.textContent = '';
    refresh();
    pop.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    addEventListener('pointerdown', outside, true);
    addEventListener('keydown', onKey, true);
  });
  toggle.addEventListener('change', () => {
    music.setEnabled(toggle.checked);
    status.textContent = '';
    refresh();
  });
  file.addEventListener('change', async () => {
    const f = file.files?.[0];
    file.value = '';
    if (!f) return;
    status.textContent = '正在读这首歌…';
    try {
      await music.useFile(f);
      // 挑了歌就是想听：关着的话顺手打开
      if (!music.enabled) music.setEnabled(true);
      status.textContent = '';
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : String(e);
    }
    refresh();
  });
  reset.addEventListener('click', () => {
    music.useDefault();
    refresh();
  });
  refresh();
  return el('div', { class: 'hud-music' }, button, pop);
}

/** 连接状态和这台设备上的离线缓存（offline.ts） */
export interface CacheControls {
  stats(): Promise<{ thumbs: number; savedAt: string | null }>;
  clear(): Promise<void>;
}

const when = (iso: string) => {
  const d = new Date(iso);
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** 标题旁边的连接状态点；点开是一个小面板：连接情况、离线缓存了多少、清空 */
function connMenu(cache: CacheControls) {
  const dot = el('span', { class: 'hud-conn', attrs: { 'data-state': 'open' } });
  const button = el('button', { class: 'hud-conn-btn', attrs: { type: 'button', 'aria-label': '连接与离线缓存', title: '实时连接正常', 'aria-haspopup': 'dialog', 'aria-expanded': 'false' } }, dot);
  const state = el('p', { class: 'conn-state' });
  const stats = el('p', { class: 'conn-stats' });
  const clear = el('button', { class: 'btn', text: '清空这台设备上的离线缓存', attrs: { type: 'button' } });
  const pop = el(
    'div',
    { class: 'conn-pop', attrs: { role: 'dialog', 'aria-label': '连接与离线缓存' } },
    state,
    stats,
    el('p', { class: 'music-note', text: '看过的缩略图和回忆的文字存在这台设备上，断网时也能看；照片和数据以服务端为准，清空后联网时会重新下载。' }),
    el('div', { class: 'music-row' }, clear),
  );
  pop.hidden = true;
  let current: 'open' | 'retrying' | 'offline' = 'open';
  const label = () => (current === 'open' ? '实时连接正常' : current === 'retrying' ? '实时连接断了，正在重连' : '连不上星海服务，显示的是离线缓存');
  const refresh = async () => {
    state.textContent = label();
    stats.textContent = '正在统计离线缓存…';
    const s = await cache.stats();
    stats.textContent = s.thumbs || s.savedAt ? `离线缓存：${s.thumbs.toLocaleString('zh-CN')} 份缩略图${s.savedAt ? `，星海是 ${when(s.savedAt)} 同步的` : ''}` : '这台设备上还没有离线缓存';
    clear.disabled = !s.thumbs && !s.savedAt;
  };
  const close = () => {
    pop.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    removeEventListener('pointerdown', outside, true);
    removeEventListener('keydown', onKey, true);
  };
  const outside = (e: PointerEvent) => {
    if (e.target instanceof Node && !pop.contains(e.target) && !button.contains(e.target)) close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    close();
    button.focus();
  };
  button.addEventListener('click', () => {
    if (!pop.hidden) return close();
    pop.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    addEventListener('pointerdown', outside, true);
    addEventListener('keydown', onKey, true);
    void refresh();
  });
  clear.addEventListener('click', async () => {
    clear.disabled = true;
    await cache.clear();
    await refresh();
  });
  return {
    node: el('div', { class: 'hud-conn-wrap' }, button, pop),
    set(s: 'open' | 'retrying' | 'offline') {
      current = s;
      dot.dataset.state = s === 'open' ? 'open' : 'retrying';
      button.title = label();
      if (!pop.hidden) state.textContent = label();
    },
  };
}

/** 按住 ms 毫秒算长按：触发以后松手的那一下点击不算，也不弹系统的长按菜单 */
function longPress(node: HTMLElement, ms: number, fire: () => void): void {
  let timer = 0, fired = false;
  node.addEventListener('pointerdown', () => {
    fired = false;
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      fired = true;
      fire();
    }, ms);
  });
  for (const type of ['pointerup', 'pointerleave', 'pointercancel']) node.addEventListener(type, () => clearTimeout(timer));
  node.addEventListener(
    'click',
    (e) => {
      if (!fired) return;
      fired = false;
      e.preventDefault();
      e.stopImmediatePropagation();
    },
    true,
  );
  node.addEventListener('contextmenu', (e) => e.preventDefault());
}

export function mountHud(
  parent: HTMLElement,
  handlers: HudHandlers & { music: MusicControls; cache: CacheControls; haptics: HapticsControls | null; onDisguise(): void },
  opts: { fake: boolean; stats: boolean },
) {
  // 压测模式没有服务，名字只是字；平时点名字改星海的名字和在一起的日子
  const name = opts.fake
    ? el('span')
    : el('button', { class: 'hud-name-btn', attrs: { type: 'button', title: '星海的名字和在一起的日子（长按藏起照片）' }, on: { click: handlers.onSettings } });
  // 长按标题：一键藏起照片（防借看，main.ts 的 setDisguise）；三指轻触、按 B 也行
  longPress(name, 700, handlers.onDisguise);
  const title = el('h1', { class: 'hud-name' }, name);
  const sub = el('p', { class: 'hud-sub' });
  const file = input({ type: 'file', multiple: '', accept: 'image/*,.heic,.heif', hidden: '' });
  file.addEventListener('change', () => {
    const files = [...(file.files ?? [])];
    file.value = '';
    if (files.length) handlers.onFiles(files);
  });
  const trail = el('button', { class: 'btn btn-icon', attrs: { type: 'button', 'aria-pressed': 'true', 'aria-label': '星轨：按时间连起所有回忆', title: '星轨：按时间连起所有回忆' }, on: { click: handlers.onTrail } }, trailIcon());
  const actions = el(
    'div',
    { class: 'hud-actions' },
    musicMenu(handlers.music, handlers.haptics),
    trail,
    el('button', { class: 'btn', text: '全景', attrs: { type: 'button', title: '回到能看到整片星海的位置（Home）' }, on: { click: handlers.onHome } }),
    opts.fake ? null : el('label', { class: 'btn btn-primary', attrs: { title: '添照片' } }, '添照片', file),
    opts.fake ? null : forTaMenu(handlers),
    opts.fake ? null : el('button', { class: 'btn', text: '邀请', attrs: { type: 'button' }, on: { click: handlers.onInvite } }),
  );
  const conn = connMenu(handlers.cache);
  const stats = el('p', { class: 'hud-stats' });
  stats.hidden = !opts.stats;
  const hint = el('p', { class: 'hud-hint', text: '拖动旋转 · 滚轮或双指缩放 · 点一颗星看照片 · Esc 退一层 · B 藏起照片' });
  const header = el('header', { class: 'hud' }, el('div', { class: 'hud-title' }, el('div', { class: 'hud-name-row' }, title, opts.fake ? null : conn.node), sub), actions);
  parent.append(header, stats, hint);
  return {
    setTitle(text: string) {
      name.textContent = text;
      document.title = `${text} · 星海画廊`;
    },
    setSub(text: string) {
      sub.textContent = text;
    },
    setStats(text: string) {
      stats.textContent = text;
    },
    setTrail(on: boolean) {
      trail.setAttribute('aria-pressed', String(on));
    },
    /** offline：打开时就连不上服务，显示的是离线缓存（连上以后会变成 open） */
    setConnection(state: 'open' | 'retrying' | 'offline') {
      conn.set(state);
    },
  };
}

// ── 照片面板 ──────────────────────────────────────────────────────

export interface PanelContent {
  date: string;
  title: string;
  place: string;
  story: string;
  by: string;
  count: string;
  aspect: number;
  /** 照片的主色：星尘烧出来时发光的边用它；压测模式没有照片，星尘也用它的渐变 */
  accent: string;
  placeholder?: string;
  swatch?: string;
  note?: string;
  /** 能不能编辑这段回忆（压测模式没有服务，不能） */
  editable: boolean;
  /** 双面翻牌的两面（flip.ts）；压测模式没有 */
  card?: CardFaces;
}

/** show 的第二个参数 */
export interface PanelShow {
  /** 在面板里换到另一张：照片用流体形变换过去 */
  sw?: PanelSwitch;
  /** 新打开：那颗星现在在屏幕上的位置，星尘从这里飞进画框 */
  from?: () => Point | null;
}

/** 切换到同一个面板里的下一张时传：照片用流体形变换过去 */
export interface PanelSwitch {
  /** 每一对照片固定的流场相位 */
  phase: number;
}

const clampAspect = (a: number) => Math.min(1.8, Math.max(0.56, a));

/**
 * 画框按照片比例定高。手机上、编辑时画框限了高度，宽度按比例封顶（style.css 里用 --ar 算）、居中：
 * 竖拍的照片完整显示，不会被裁成一条，也不会贴在左边
 */
function setAspect(photo: HTMLElement, aspect: number): void {
  const a = String(clampAspect(aspect));
  photo.style.aspectRatio = a;
  photo.style.setProperty('--ar', a);
}

/** 压测模式没有照片：画一张和面板底色一样的渐变给星尘取色 */
export function swatchCanvas(color: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 48;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(19, 14, 0, 19, 14, Math.hypot(45, 34));
  grad.addColorStop(0, color);
  grad.addColorStop(1, '#0b0d1a');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 48);
  return c;
}

/** 保存一面时撞上另一台设备刚改过：带上那边的版本号，再点保存就在它上面改（字还是框里的） */
export class SideConflict extends Error {
  readonly base: string | null;
  constructor(message: string, base: string | null) {
    super(message);
    this.base = base;
  }
}

/**
 * 翻牌上写自己那一面的小表单：⌘ / Ctrl + 回车保存，Esc 和「取消」一样（写了东西先问一句）。
 * 版本号在打开表单时记下（同 ADR-0009）：这期间另一台设备改过，保存时会被退回，不会悄悄盖掉。
 * 保存失败时字留在框里、下面一行说为什么。
 */
function sideEditor(o: { value: string; base: string | null; placeholder: string; onSave(text: string, base: string | null): Promise<void>; onCancel(): void }) {
  let base = o.base;
  const area = el('textarea', { class: 'flip-input', attrs: { rows: '3', maxlength: String(LIMITS.perspectiveLength), placeholder: o.placeholder, 'aria-label': o.placeholder } });
  area.value = o.value;
  const err = el('p', { class: 'form-error flip-error', attrs: { role: 'alert' } });
  const save = el('button', { class: 'btn btn-primary', text: '保存', attrs: { type: 'button' } });
  let busy = false;
  const submit = async () => {
    if (busy) return;
    busy = true;
    save.disabled = true;
    err.textContent = '';
    try {
      await o.onSave(area.value, base);
    } catch (e) {
      if (e instanceof SideConflict) base = e.base;
      err.textContent = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
      save.disabled = false;
    }
  };
  const cancel = async () => {
    if (area.value.trim() !== o.value.trim() && !(await confirmDialog({ title: '放弃这次写的吗？', text: '框里的字不会保存。', confirm: '放弃' }))) return;
    o.onCancel();
  };
  save.addEventListener('click', () => void submit());
  area.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.isComposing) {
      e.preventDefault();
      void submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      void cancel();
    }
  });
  const node = el(
    'div',
    { class: 'flip-editor' },
    area,
    err,
    el('div', { class: 'flip-editor-row' }, save, el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' }, on: { click: () => void cancel() } })),
  );
  return { node, focus: () => area.focus({ preventScroll: true }) };
}

export function mountPanel(
  parent: HTMLElement,
  handlers: {
    onClose(): void;
    onPrev(): void;
    onNext(): void;
    onRing(): void;
    onEdit(focus?: FieldName): void;
    onDelete(): void;
    /**
     * 保存自己在这张照片上的那一面（null 是清空），base 是打开表单时这一面的版本号。
     * 失败时抛出的错误显示在表单下面；另一台设备刚改过时抛 SideConflict
     */
    onSaveSide(text: string | null, base: string | null): Promise<void>;
  },
  opts: { morphPoints: number; dust: Stardust; reduced: boolean },
) {
  const img = el('img', { attrs: { alt: '', decoding: 'async' } });
  const note = el('figcaption', { class: 'panel-note' });
  const photo = el('figure', { class: 'panel-photo' }, img, note);

  // ── 双面翻牌（ADR-0011）：正面是照片和上传的人写的，背面是另一个人的视角 ──
  const saidBy = el('p', { class: 'flip-by' });
  const saidText = el('p', { class: 'flip-said' });
  const saidNote = el('button', { class: 'flip-note', attrs: { type: 'button' } });
  const saidSlot = el('div', { class: 'flip-slot' });
  const said = el('div', { class: 'flip-said-wrap' }, saidBy, saidText, saidNote, saidSlot);
  const turn = el('button', { class: 'flip-turn', attrs: { type: 'button' } });
  const resonance = el('span', { class: 'flip-resonance', text: '✦✦', attrs: { title: '双星共鸣：两个人都写了这张照片', 'aria-label': '双星共鸣' } });
  // 翻面按钮和双星压在照片的角上（放在画框外面一层：画框会被形变、星尘的画布盖住）
  const front = el('div', { class: 'flip-face flip-front' }, el('div', { class: 'flip-photo' }, photo, turn, resonance), said);
  const backBg = el('div', { class: 'flip-back-bg', attrs: { 'aria-hidden': 'true' } });
  const backWho = el('p', { class: 'flip-back-who' });
  const backText = el('p', { class: 'flip-back-text' });
  const backNote = el('p', { class: 'flip-back-note' });
  const backEdit = el('button', { class: 'flip-back-edit', text: '改一改', attrs: { type: 'button' } });
  const backSlot = el('div', { class: 'flip-slot' });
  const backReturn = el('button', { class: 'flip-turn flip-turn-back', text: '↻ 返回照片', attrs: { type: 'button' } });
  const back = el('div', { class: 'flip-face flip-back' }, backBg, el('div', { class: 'flip-back-body' }, backWho, backText, backNote, backEdit, backSlot), backReturn);
  back.inert = true;
  const inner = el('div', { class: 'flip-inner' }, front, back);
  const tilt = el('div', { class: 'flip-tilt' }, inner);
  const card = el('div', { class: 'flip' }, tilt);
  const date = el('p', { class: 'panel-date' });
  const title = el('h2', { class: 'panel-title' });
  const place = el('p', { class: 'panel-place' });
  const story = el('p', { class: 'panel-story' });
  const by = el('p', { class: 'panel-by' });
  const editButton = el('button', { class: 'btn btn-icon btn-ghost panel-tool', attrs: { type: 'button', 'aria-label': '编辑这段回忆', title: '写标题、地点、故事和心动' }, on: { click: () => handlers.onEdit() } }, pencilIcon());
  const deleteButton = el('button', { class: 'btn btn-icon btn-ghost panel-tool', attrs: { type: 'button', 'aria-label': '删除这张照片', title: '删除这张照片（先放进回收站）' }, on: { click: () => handlers.onDelete() } }, trashIcon());
  // 没写故事的日子：一行很淡的邀请，点了直接写故事
  const invite = el('button', { class: 'panel-invite', text: '写下这一天的故事…', attrs: { type: 'button' }, on: { click: () => handlers.onEdit('story') } });
  const view = el('div', { class: 'panel-view' }, title, place, story, invite, by);
  /** 编辑的表单放这里；放了表单时上面的文字藏起来 */
  const editorSlot = el('div', { class: 'panel-editor' });
  const count = el('span', { class: 'panel-count' });
  const prev = el('button', { class: 'btn btn-ghost', text: '‹ 上一张', attrs: { type: 'button' }, on: { click: handlers.onPrev } });
  const next = el('button', { class: 'btn btn-ghost', text: '下一张 ›', attrs: { type: 'button' }, on: { click: handlers.onNext } });
  const ring = el('button', { class: 'btn btn-ghost', text: '轮转', attrs: { type: 'button', title: '把这一天的照片排成一圈，转着看' }, on: { click: handlers.onRing } });
  const panel = el(
    'aside',
    { class: 'panel', attrs: { 'aria-label': '照片' } },
    el('button', { class: 'panel-close', text: '×', attrs: { type: 'button', 'aria-label': '关闭' }, on: { click: handlers.onClose } }),
    card,
    el('div', { class: 'panel-body' }, el('div', { class: 'panel-head' }, date, el('div', { class: 'panel-tools' }, editButton, deleteButton)), view, editorSlot),
    el('nav', { class: 'panel-nav' }, prev, count, ring, next),
  );
  panel.hidden = true;
  parent.append(panel);
  const morph = new PhotoMorph(photo, { points: opts.morphPoints, reduced: opts.reduced });
  const dust = opts.dust;
  /** 现在显示的内容（关上时星尘要用它的主色和底色） */
  let current: PanelContent | null = null;
  let url: string | null = null;
  /** 正在等下一张的清晰图，到了再形变；等太久就直接换 */
  let pending: { c: PanelContent; phase: number; timer: ReturnType<typeof setTimeout> } | null = null;
  /** 每次换内容加一，晚到的解码结果据此作废 */
  let gen = 0;

  const setSrc = (next: string | null) => {
    if (url && url !== next) URL.revokeObjectURL(url);
    url = next;
    if (next) {
      img.src = next;
      img.hidden = false;
      backBg.style.backgroundImage = `url("${next}")`;
    }
  };

  // ── 翻牌 ──
  let flipped = false;
  /** 正在写的是哪一面（同一时间只开一个表单） */
  let writing: { side: 'front' | 'back'; focus(): void } | null = null;
  const setFlipped = (on: boolean, animate = true) => {
    if (flipped === on) return;
    flipped = on;
    // 换照片时翻回正面不要动画
    card.classList.toggle('flip-instant', !animate || opts.reduced);
    card.classList.toggle('flip-flipped', on);
    front.inert = on;
    back.inert = !on;
    if (animate) setTimeout(tickBuzz, 220);
    // 翻到背面、背面是自己要写的：等翻过来再把光标放进去
    if (on && writing?.side === 'back') setTimeout(() => writing?.focus(), opts.reduced ? 0 : 420);
    if (!animate) requestAnimationFrame(() => card.classList.remove('flip-instant'));
  };
  const closeWriting = () => {
    writing = null;
    saidSlot.replaceChildren();
    backSlot.replaceChildren();
  };
  const openWriting = (side: 'front' | 'back', f: CardFace) => {
    closeWriting();
    const ed = sideEditor({
      value: f.text,
      base: f.base,
      placeholder: side === 'front' ? '写下这张照片的心情…' : '写下你眼中的这一天…',
      onSave: async (text, base) => {
        await handlers.onSaveSide(text.trim() ? text : null, base);
        closeWriting();
        if (current) showCard(current);
      },
      onCancel: () => {
        closeWriting();
        if (current) showCard(current);
      },
    });
    writing = { side, focus: ed.focus };
    (side === 'front' ? saidSlot : backSlot).append(ed.node);
    if (current) showCard(current);
    if (side === 'front' || flipped) ed.focus();
  };
  turn.addEventListener('click', () => setFlipped(true));
  backReturn.addEventListener('click', () => setFlipped(false));
  saidNote.addEventListener('click', () => current?.card?.front.mine && openWriting('front', current.card.front));
  saidText.addEventListener('click', () => current?.card?.front.mine && openWriting('front', current.card.front));
  backEdit.addEventListener('click', () => current?.card && openWriting('back', current.card.back));
  // 手机上左右轻扫翻面；在表单、按钮上滑不算
  let swipe: { x: number; y: number } | null = null;
  card.addEventListener('pointerdown', (e) => {
    swipe = e.target instanceof Element && e.target.closest('textarea, button') ? null : { x: e.clientX, y: e.clientY };
  });
  card.addEventListener('pointerup', (e) => {
    const s = swipe;
    swipe = null;
    if (!s || !current?.card) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) setFlipped(!flipped);
  });
  // 桌面上鼠标停在卡上：跟着位置微微偏转（最多 5°）
  if (!opts.reduced && matchMedia('(hover: hover)').matches) {
    card.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !current?.card) return;
      const r = card.getBoundingClientRect();
      tilt.style.setProperty('--ry', `${(((e.clientX - r.left) / r.width - 0.5) * 10).toFixed(2)}deg`);
      tilt.style.setProperty('--rx', `${(-((e.clientY - r.top) / r.height - 0.5) * 6).toFixed(2)}deg`);
    });
    card.addEventListener('pointerleave', () => {
      tilt.style.removeProperty('--ry');
      tilt.style.removeProperty('--rx');
    });
  }

  /** 两面的字；正在写的那一面只换署名，不动表单 */
  const showCard = (c: PanelContent) => {
    const f = c.card;
    card.classList.toggle('flip-plain', !f);
    if (!f) {
      said.hidden = turn.hidden = resonance.hidden = true;
      if (flipped) setFlipped(false, false);
      return;
    }
    turn.hidden = false;
    turn.textContent = f.turnLabel;
    turn.classList.toggle('flip-turn-warm', f.back.written && !f.back.mine);
    resonance.hidden = !f.resonance;
    // 正面：上传的人写的那一两行
    const fw = writing?.side === 'front';
    saidBy.textContent = f.front.written && !f.front.hidden && f.front.createdAt ? `✦ ${f.front.name} 写于 ${writtenOn(f.front.createdAt)}` : '';
    saidBy.hidden = !saidBy.textContent || fw;
    saidText.textContent = f.front.text;
    saidText.hidden = !f.front.text || fw;
    saidText.classList.toggle('flip-said-mine', f.front.mine);
    saidNote.textContent = f.frontNote;
    saidNote.hidden = !f.frontNote || fw;
    saidNote.disabled = !f.front.mine;
    said.hidden = saidBy.hidden && saidText.hidden && saidNote.hidden && !fw;
    // 背面：另一个人的视角
    const bw = writing?.side === 'back';
    backWho.textContent = `✦ ${f.back.mine ? '我' : f.back.name} 眼中的这一天`;
    backText.textContent = f.back.text;
    backText.hidden = !f.back.text || bw;
    backNote.textContent = f.backNote;
    backNote.hidden = !f.backNote || bw;
    backEdit.hidden = !f.back.mine || !f.back.written || bw;
    // 背面是自己的、还没写：翻过去就是输入框
    if (f.back.mine && !f.back.written && !writing) openWriting('back', f.back);
  };
  const editing = () => editorSlot.childElementCount > 0;
  const showText = (c: PanelContent) => {
    date.textContent = c.date;
    title.textContent = c.title;
    place.textContent = c.place;
    place.hidden = !c.place;
    story.textContent = c.story;
    story.hidden = !c.story;
    invite.hidden = !c.editable || Boolean(c.story);
    editButton.hidden = !c.editable || editing();
    deleteButton.hidden = !c.editable || editing();
    by.textContent = c.by;
    count.textContent = c.count;
    prev.disabled = next.disabled = c.count === '1 / 1';
    // 这一天只有一张时没什么可转的
    ring.hidden = c.count === '1 / 1';
    showCard(c);
  };
  const showPhoto = (c: PanelContent) => {
    setAspect(photo, c.aspect);
    photo.style.height = '';
    photo.style.background = c.swatch ?? '';
    setSrc(null);
    img.style.opacity = '';
    img.src = c.placeholder ?? '';
    img.hidden = !c.placeholder;
    note.textContent = c.note ?? '';
    note.hidden = !c.note;
  };
  const cancelPending = () => {
    if (pending) clearTimeout(pending.timer);
    pending = null;
  };
  const cornerRadius = () => parseFloat(getComputedStyle(photo).borderTopLeftRadius) || 0;
  /** 画框里现在能拿去给星尘取色的图：照片（已解码）或者压测模式的渐变 */
  const dustImage = (c: PanelContent): DustImage | null =>
    !img.hidden && img.complete && img.naturalWidth > 0 ? img : c.swatch ? swatchCanvas(c.accent) : null;

  /** 新打开：先藏起画框里的图，星尘从星飞进来凝聚成照片，烧完再露出来 */
  const assemble = (c: PanelContent, from: () => Point | null) => {
    const mine = gen;
    photo.style.background = '';
    img.style.opacity = '0';
    const reveal = () => {
      if (mine !== gen) return;
      img.style.opacity = '';
      photo.style.background = c.swatch ?? '';
      img.removeEventListener('load', refine);
    };
    // 先用主色的渐变；缩略图、清晰图解码好了就换上
    const refine = () => {
      if (mine !== gen) return;
      img.decode().then(
        () => mine === gen && dust.running && dust.setImage(img),
        () => undefined,
      );
    };
    img.addEventListener('load', refine);
    const ok = dust.open({ image: swatchCanvas(c.accent), accent: c.accent, radius: cornerRadius(), from, frame: () => photo.getBoundingClientRect(), onDone: reveal });
    if (!ok) reveal();
    else if (img.complete && img.naturalWidth > 0) refine();
  };

  /** 从正显示的这张流动着变成 to；画框高度跟着从这张的比例变到下一张的比例 */
  const startMorph = (c: PanelContent, to: HTMLImageElement, toUrl: string, phase: number) => {
    const width = photo.clientWidth, fromHeight = photo.getBoundingClientRect().height;
    // 先按下一张的比例量出画框高度，再钉回当前高度，从这里开始变（同一段同步代码里，不会闪）
    setAspect(photo, c.aspect);
    const toHeight = photo.getBoundingClientRect().height;
    // 形变的画布宽度是固定的：手机上横拍和竖拍之间换时画框宽度差得多，直接换，不做形变
    if (Math.abs(photo.clientWidth - width) > width * 0.1) {
      showPhoto(c);
      setSrc(toUrl);
      return;
    }
    photo.style.height = `${fromHeight}px`;
    let swapped = false;
    const ok = morph.start({
      from: img,
      to,
      width,
      fromHeight,
      toHeight,
      phase,
      onFrame(h, p, particles) {
        photo.style.height = `${h.toFixed(2)}px`;
        // 中点之前淡出的是上一张，之后淡入的是下一张；中段只剩粒子
        if (!swapped && p >= 0.5) {
          swapped = true;
          setSrc(toUrl);
        }
        img.style.opacity = String(1 - particles);
      },
      onDone() {
        if (!swapped) setSrc(toUrl);
        photo.style.height = '';
        img.style.opacity = '';
      },
    });
    if (!ok) {
      showPhoto(c);
      setSrc(toUrl);
    }
  };

  return {
    /** sw：在面板里换到下一张；照片等清晰图到了再形变过去 */
    show(c: PanelContent, how: PanelShow = {}) {
      // 还在凝聚的星尘直接结束，露出图片，再换内容
      dust.finish();
      gen++;
      morph.finish();
      cancelPending();
      const wasOpen = !panel.hidden, sw = how.sw;
      // 换了一张：翻回正面，没保存的那一面不留（每次换照片都从正面看起）
      closeWriting();
      setFlipped(false, false);
      backBg.style.backgroundImage = c.placeholder ? `url("${c.placeholder}")` : '';
      current = c;
      panel.hidden = false;
      showText(c);
      if (sw && wasOpen && !img.hidden && img.complete && img.naturalWidth > 0) {
        note.hidden = true;
        const timer = setTimeout(() => {
          pending = null;
          showPhoto(c);
        }, 1500);
        pending = { c, phase: sw.phase, timer };
        return;
      }
      showPhoto(c);
      if (!wasOpen && how.from && !opts.reduced) assemble(c, how.from);
    },
    /** 清晰的图到了 */
    setImage(blob: Blob) {
      const next = URL.createObjectURL(blob);
      const wait = pending;
      if (!wait) return setSrc(next);
      cancelPending();
      const mine = gen, to = new Image();
      to.src = next;
      to.decode().then(
        () => {
          if (mine !== gen) return URL.revokeObjectURL(next);
          startMorph(wait.c, to, next, wait.phase);
        },
        () => {
          if (mine !== gen) return URL.revokeObjectURL(next);
          showPhoto(wait.c);
          setSrc(next);
        },
      );
    },
    /** 只换文字（回忆被改了、在一起的日子改了），照片不动 */
    text(c: PanelContent) {
      if (panel.hidden) return;
      current = c;
      showText(c);
    },
    /** 放进编辑的表单（文字藏起来）；null 收起表单，回到文字 */
    edit(node: HTMLElement | null) {
      editorSlot.replaceChildren(...(node ? [node] : []));
      view.hidden = node !== null;
      editButton.hidden = deleteButton.hidden = node !== null || !current?.editable;
      panel.classList.toggle('panel-editing', node !== null);
    },
    get editing(): boolean {
      return editing();
    },
    /** quiet：不放关上时的星尘（进轮转时，照片换成从星里飞起的卡片） */
    hide(o: { quiet?: boolean } = {}) {
      dust.finish();
      morph.finish();
      // 照片烧掉，星尘像天灯一样升起（拿当前的图和画框位置，面板随后就藏起来）
      const c = current, image = c && !panel.hidden && !opts.reduced && !o.quiet ? dustImage(c) : null;
      if (c && image) dust.close({ image, accent: c.accent, radius: cornerRadius(), rect: photo.getBoundingClientRect() });
      gen++;
      cancelPending();
      current = null;
      panel.hidden = true;
      setSrc(null);
      img.removeAttribute('src');
      editorSlot.replaceChildren();
      view.hidden = false;
      panel.classList.remove('panel-editing');
      closeWriting();
      setFlipped(false, false);
      backBg.style.backgroundImage = '';
    },
    get open() {
      return !panel.hidden;
    },
    /** 翻面（空格、F 键）；压测模式的照片没有背面 */
    flip() {
      if (current?.card) setFlipped(!flipped);
    },
    /** 正在写翻牌上的一面（这时空格、F 是打字） */
    get writingSide(): boolean {
      return writing !== null;
    },
  };
}

// ── 编辑回忆 ─────────────────────────────────────────────────────

export interface EditorOptions {
  values: MemoryFields;
  /** 没写标题、地点时显示的（「在一起的第 N 天」、照片里的经纬度） */
  placeholders: { title: string; place: string };
  /** 打开时就显示的说明，比如「接着上次没保存的修改」 */
  note?: string;
  /** 和打开时比改没改过：取消时改过的话先问一句 */
  dirty(values: MemoryFields): boolean;
  onSave(values: MemoryFields): void;
  onCancel(): void;
}

/** 撞上 Ta 刚改过、同一个字段两个人写得不一样时，表单里显示的 */
export interface EditorConflict {
  text: string;
  /** Ta 写的：字段名和 Ta 的版本（清空了是空字符串） */
  theirs: { label: string; value: string }[];
  onMine(): void;
  onTheirs(): void;
}

/**
 * 照片面板里的编辑表单：标题、地点、故事、心动（1–5）。保存、冲突时怎么办由 main.ts 决定，这里只管表单。
 * 回车在标题、地点里是跳到下一格（输入法选字的回车不算）；故事里 ⌘/Ctrl + 回车保存；Esc 和「取消」一样。
 */
export function memoryEditor(opts: EditorOptions) {
  const title = input({ name: 'title', maxlength: String(LIMITS.titleLength), placeholder: opts.placeholders.title, autocomplete: 'off', enterkeyhint: 'next' });
  const place = input({ name: 'place', maxlength: String(LIMITS.placeLength), placeholder: opts.placeholders.place || '比如：外滩', autocomplete: 'off', enterkeyhint: 'next' });
  const story = el('textarea', { attrs: { name: 'story', rows: '6', maxlength: String(LIMITS.storyLength), placeholder: '那天发生了什么，你们说了什么，想留给以后的自己什么……' } });
  let shine: Shine = opts.values.shine;
  const marks = ([1, 2, 3, 4, 5] as const).map((n) => {
    const radio = input({ type: 'radio', name: 'shine', value: String(n), 'aria-label': `心动 ${n}` });
    radio.addEventListener('change', () => {
      shine = n;
      paint();
    });
    return { radio, label: el('label', { class: 'shine-mark', attrs: { title: `心动 ${n}` } }, radio, sparkIcon()) };
  });
  const paint = () =>
    marks.forEach((m, k) => {
      m.radio.checked = k + 1 === shine;
      m.label.classList.toggle('on', k < shine);
    });
  const note = el('p', { class: 'editor-note', attrs: { role: 'status' } });
  const conflictBox = el('div', { class: 'editor-conflict', attrs: { role: 'alert' } });
  conflictBox.hidden = true;
  const err = formError();
  const save = el('button', { class: 'btn btn-primary', text: '保存', attrs: { type: 'submit' } });
  const cancel = el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' } });
  const actions = el('div', { class: 'editor-actions' }, save, cancel);
  const keep = el('button', { class: 'btn', text: '继续写', attrs: { type: 'button' } });
  const discard = el('button', { class: 'btn btn-ghost', text: '放弃修改', attrs: { type: 'button' } });
  const confirm = el('div', { class: 'editor-confirm' }, el('p', { text: '还没保存，放弃这次的修改吗？' }), el('div', { class: 'editor-actions' }, keep, discard));
  confirm.hidden = true;
  const fields = el(
    'fieldset',
    { class: 'editor-fields' },
    field('标题', title),
    field('地点', place),
    field('故事', story),
    el(
      'fieldset',
      { class: 'shine' },
      el('legend', { class: 'field-label', text: '心动' }),
      el('div', { class: 'shine-marks' }, ...marks.map((m) => m.label)),
      el('span', { class: 'field-hint', text: '越心动，这一天的星越大' }),
    ),
  );
  const form = el('form', { class: 'editor', attrs: { 'aria-label': '编辑这段回忆' } }, fields, note, conflictBox, err, actions, confirm);

  const values = (): MemoryFields => ({ title: title.value, place: place.value, story: story.value, shine });
  const set = (v: Partial<MemoryFields>) => {
    if (v.title !== undefined) title.value = v.title;
    if (v.place !== undefined) place.value = v.place;
    if (v.story !== undefined) story.value = v.story;
    if (v.shine !== undefined) shine = v.shine;
    paint();
  };
  const askCancel = () => {
    if (!opts.dirty(values())) return opts.onCancel();
    actions.hidden = true;
    confirm.hidden = false;
    keep.focus();
  };
  set(opts.values);
  note.textContent = opts.note ?? '';
  // 输入法正在选字时的回车（Safari 上是 keyCode 229）不算
  const composing = (e: KeyboardEvent) => e.isComposing || e.keyCode === 229;
  title.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !composing(e)) {
      e.preventDefault();
      place.focus();
    }
  });
  place.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !composing(e)) {
      e.preventDefault();
      story.focus();
    }
  });
  story.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !composing(e)) {
      e.preventDefault();
      form.requestSubmit();
    }
  });
  // Esc 留在表单里处理（取消编辑），不让它冒到 main.ts 去关面板
  form.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || composing(e)) return;
    e.preventDefault();
    e.stopPropagation();
    if (!confirm.hidden) {
      confirm.hidden = true;
      actions.hidden = false;
      return;
    }
    askCancel();
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    opts.onSave(values());
  });
  cancel.addEventListener('click', askCancel);
  keep.addEventListener('click', () => {
    confirm.hidden = true;
    actions.hidden = false;
    story.focus();
  });
  discard.addEventListener('click', () => opts.onCancel());

  return {
    node: form,
    values,
    set,
    focus(which: FieldName = 'title') {
      const target = which === 'place' ? place : which === 'story' ? story : which === 'shine' ? marks[shine - 1]!.radio : title;
      target.focus({ preventScroll: false });
      if (target === story) story.setSelectionRange(story.value.length, story.value.length);
    },
    /** 正在保存：表单锁住，免得保存途中又改了 */
    busy(on: boolean) {
      fields.disabled = on;
      save.disabled = cancel.disabled = on;
      save.textContent = on ? '正在保存…' : '保存';
    },
    error(text: string) {
      err.textContent = text;
    },
    note(text: string) {
      note.textContent = text;
    },
    conflict(c: EditorConflict | null) {
      conflictBox.hidden = c === null;
      actions.hidden = c !== null;
      if (!c) return conflictBox.replaceChildren();
      // 冲突框里已经说了 Ta 改过，上面那句提醒不用再留着
      note.textContent = '';
      const mine = el('button', { class: 'btn btn-primary', text: '保留我写的，保存', attrs: { type: 'button' }, on: { click: c.onMine } });
      conflictBox.replaceChildren(
        el('p', { class: 'editor-conflict-text', text: c.text }),
        el('dl', {}, ...c.theirs.flatMap((t) => [el('dt', { text: t.label }), el('dd', { text: t.value || '（清空了）' })])),
        el('div', { class: 'editor-actions' }, mine, el('button', { class: 'btn', text: '改用 Ta 写的', attrs: { type: 'button' }, on: { click: c.onTheirs } })),
      );
      mine.focus();
    },
  };
}

export type MemoryEditor = ReturnType<typeof memoryEditor>;

// ── 轮转的字幕 ───────────────────────────────────────────────────

/** 光环底下的字幕：正面那张的日期、标题、地点和第几张，加一个「返回」 */
export function mountCaption(parent: HTMLElement, handlers: { onBack(): void }) {
  const date = el('p', { class: 'caption-date' });
  const title = el('p', { class: 'caption-title' });
  const meta = el('p', { class: 'caption-meta' });
  const touch = matchMedia('(pointer: coarse)').matches;
  const root = el(
    'div',
    { class: 'caption', attrs: { 'aria-live': 'polite' } },
    date,
    title,
    meta,
    el(
      'div',
      { class: 'caption-row' },
      el('p', { class: 'caption-hint', text: touch ? '左右滑动转动 · 点正中那张打开' : '拖动、滚轮或 ← → 转动 · 点正中那张打开' }),
      el('button', { class: 'btn', text: '返回', attrs: { type: 'button' }, on: { click: handlers.onBack } }),
    ),
  );
  root.hidden = true;
  parent.append(root);
  return {
    show(c: { date: string; title: string; place: string; count: string }) {
      date.textContent = c.date;
      title.textContent = c.title;
      meta.textContent = c.place ? `${c.place} · ${c.count}` : c.count;
      root.hidden = false;
    },
    hide() {
      root.hidden = true;
    },
  };
}

// ── 提示 ─────────────────────────────────────────────────────────

export interface ToastHandle {
  update(text: string): void;
  progress(fraction: number): void;
  done(text: string, action?: { label: string; onClick: () => void }, ms?: number): void;
  close(): void;
}

export function toast(parent: HTMLElement, text: string, opts: { action?: { label: string; onClick: () => void }; ms?: number; sticky?: boolean } = {}): ToastHandle {
  const label = el('span', { class: 'toast-text', text });
  const bar = el('span', { class: 'toast-bar' });
  const node = el('div', { class: 'toast' }, label);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const close = () => {
    clearTimeout(timer);
    node.classList.add('toast-out');
    setTimeout(() => node.remove(), 300);
  };
  const setAction = (a?: { label: string; onClick: () => void }) => {
    node.querySelector('.toast-action')?.remove();
    if (a)
      node.append(
        el('button', {
          class: 'toast-action',
          text: a.label,
          attrs: { type: 'button' },
          on: {
            click: () => {
              a.onClick();
              close();
            },
          },
        }),
      );
  };
  setAction(opts.action);
  const arm = (ms: number) => {
    clearTimeout(timer);
    timer = setTimeout(close, ms);
  };
  if (!opts.sticky) arm(opts.ms ?? 4200);
  parent.append(node);
  return {
    update: (t) => (label.textContent = t),
    progress(f) {
      if (!bar.isConnected) node.append(bar);
      bar.style.transform = `scaleX(${Math.max(0, Math.min(1, f)).toFixed(3)})`;
    },
    done(t, action, ms = 6000) {
      label.textContent = t;
      bar.remove();
      setAction(action);
      arm(ms);
    },
    close,
  };
}

// ── 邀请 ─────────────────────────────────────────────────────────

export function inviteDialog(opts: { canInviteMember: boolean; create: (kind: InviteKind) => Promise<InviteResponse> }): void {
  const body = el('div', { class: 'dialog-body' });
  const err = formError();
  const dialog = el('dialog', { class: 'dialog' }, body, err);
  const close = () => {
    dialog.close();
    dialog.remove();
  };
  const choose = async (kind: InviteKind) => {
    err.textContent = '';
    try {
      // 二维码的编码器只在邀请时用到，用的时候才加载，不放进首屏的脚本
      const [inv, { qrSvg }] = await Promise.all([opts.create(kind), import('./qr.ts')]);
      const link = `${location.origin}${location.pathname}#invite=${inv.code}`;
      const linkInput = input({ readonly: '', value: link, 'aria-label': '邀请链接' });
      const copy = el('button', { class: 'btn btn-primary', text: '复制链接', attrs: { type: 'button' } });
      copy.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(link);
          copy.textContent = '已复制';
        } catch {
          linkInput.select();
          copy.textContent = '已选中，按 ⌘C / Ctrl+C 复制';
        }
      });
      const hours = Math.round((Date.parse(inv.expiresAt) - Date.now()) / 3_600_000);
      body.replaceChildren(
        el('h2', { class: 'dialog-title', text: kind === 'member' ? '把这个链接发给 Ta' : '在另一台设备上打开这个链接' }),
        el('p', { class: 'card-text', text: `${hours} 小时内有效，只能用一次。也可以在加入页面手动输入邀请码。` }),
        el(
          'figure',
          { class: 'invite-qr' },
          qrSvg(link, '邀请链接的二维码'),
          el('figcaption', { text: kind === 'member' ? '或者让 Ta 用手机相机扫一扫' : '或者用那台设备的相机扫一扫' }),
        ),
        linkInput,
        el('p', { class: 'invite-code', text: formatCode(inv.code) }),
        el('div', { class: 'dialog-actions' }, copy, el('button', { class: 'btn', text: '好了', attrs: { type: 'button' }, on: { click: close } })),
      );
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : String(e);
    }
  };
  body.append(
    el('h2', { class: 'dialog-title', text: '邀请' }),
    el('p', { class: 'card-text', text: opts.canInviteMember ? '邀请 Ta 加入这片星海，或者把你的另一台设备加进来。' : '星海里已经有你们两个人了。可以把你的另一台设备加进来。' }),
    el(
      'div',
      { class: 'dialog-actions' },
      opts.canInviteMember ? el('button', { class: 'btn btn-primary', text: '邀请 Ta', attrs: { type: 'button' }, on: { click: () => void choose('member') } }) : null,
      el('button', { class: 'btn', text: '加我的另一台设备', attrs: { type: 'button' }, on: { click: () => void choose('device') } }),
      el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' }, on: { click: close } }),
    ),
  );
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  document.body.append(dialog);
  dialog.showModal();
}

// ── 星海设置 ─────────────────────────────────────────────────────

/** 点顶栏的名字：改星海的名字和在一起的日子 */
export interface SpaceValues {
  name: string;
  since: LocalDate;
  /** 入场的那段话；空字符串是用默认文案 */
  introText: string;
  introHold: number;
  /** 写前互盲（双面翻牌） */
  blindPerspectives: boolean;
}

export function spaceDialog(opts: SpaceValues & { introPlaceholder: string; save(v: SpaceValues): Promise<void>; onTrash(): void; onReplay(): void }): void {
  const name = input({ name: 'spaceName', required: '', maxlength: String(LIMITS.spaceNameLength), value: opts.name });
  const since = input({ name: 'since', type: 'date', required: '', max: localDateOf(new Date()), value: opts.since });
  const words = el('textarea', { attrs: { name: 'introText', rows: '3', maxlength: String(LIMITS.introTextLength), placeholder: opts.introPlaceholder } });
  words.value = opts.introText;
  const hold = input({ name: 'introHold', type: 'number', required: '', min: '0', max: String(LIMITS.introHoldMax), step: '0.5', inputmode: 'decimal', value: String(opts.introHold) });
  const blind = input({ name: 'blindPerspectives', type: 'checkbox', role: 'switch' });
  blind.checked = opts.blindPerspectives;
  const err = formError();
  const dialog = el('dialog', { class: 'dialog' });
  const close = () => {
    dialog.close();
    dialog.remove();
  };
  const values = (): SpaceValues => ({ name: name.value.trim(), since: since.value as LocalDate, introText: words.value.trim(), introHold: Number(hold.value), blindPerspectives: blind.checked });
  // 重看完整入场要刷新页面：先把改了的存好，免得写到一半的那段话丢了
  const replay = () => {
    if (!form.reportValidity()) return;
    void submitting(form, err, async () => {
      await opts.save(values());
      close();
      opts.onReplay();
    });
  };
  const form = el(
    'form',
    { class: 'form' },
    el('h2', { class: 'dialog-title', text: '星海' }),
    field('星海的名字', name),
    field('在一起的日子', since, '螺线从这一天开始，每过一年向外绕一圈。改了以后，所有的星会按新的日子重新排，Ta 那边也一样。'),
    field('入场的那段话', words, `爱心炸开以后浮现，停一会儿化成星尘飘走；平时打开星海时在顶部停两秒。空着就显示上面这句默认的。最多 ${LIMITS.introLines} 行，两个人看到的一样。`),
    field('停留几秒', hold, `默认 ${LIMITS.introHold} 秒，最长 ${LIMITS.introHoldMax} 秒；填 0 就不显示这段话。`),
    el(
      'div',
      { class: 'field' },
      el('label', { class: 'music-switch' }, blind, el('span', { text: '写前互盲' })),
      el('p', { class: 'field-hint', text: '照片翻过来，背面是另一个人的视角。打开以后，写下自己那一面之前看不到对方写的，写完两边一起揭晓，免得后写的照着先写的写。两个人一样。' }),
    ),
    err,
    el(
      'div',
      { class: 'dialog-actions' },
      el('button', { class: 'btn btn-primary', text: '保存', attrs: { type: 'submit' } }),
      el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' }, on: { click: close } }),
    ),
    el(
      'div',
      { class: 'dialog-section' },
      el('p', { class: 'field-hint', text: '平时打开星海，星从中心的光点里绽开，不用等；在一起的整周年、每满 100 天会升起极光；每台设备第一次打开时是完整的心跳和爆炸。' }),
      el('button', { class: 'btn', text: '保存并重看完整入场', attrs: { type: 'button' }, on: { click: replay } }),
    ),
    el(
      'div',
      { class: 'dialog-section' },
      el('p', { class: 'field-hint', text: `删了的照片在回收站里留 ${LIMITS.trashDays} 天，你们两个人都能恢复。` }),
      el(
        'button',
        {
          class: 'btn',
          text: '打开回收站',
          attrs: { type: 'button' },
          on: {
            click: () => {
              close();
              opts.onTrash();
            },
          },
        },
        trashIcon(),
      ),
    ),
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void submitting(form, err, async () => {
      await opts.save(values());
      close();
    });
  });
  dialog.append(form);
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  document.body.append(dialog);
  dialog.showModal();
}

// ── 确认、回收站 ─────────────────────────────────────────────────

/** 做不好挽回的事之前问一句：Esc、「取消」都算不做；默认焦点在「取消」上，免得一个回车就删了 */
export function confirmDialog(opts: { title: string; text: string; confirm: string; danger?: boolean }): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = el('dialog', { class: 'dialog dialog-confirm' });
    const done = (ok: boolean) => {
      dialog.close();
      dialog.remove();
      resolve(ok);
    };
    const cancel = el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' }, on: { click: () => done(false) } });
    dialog.append(
      el('h2', { class: 'dialog-title', text: opts.title }),
      el('p', { class: 'card-text', text: opts.text }),
      el('div', { class: 'dialog-actions' }, el('button', { class: `btn ${opts.danger ? 'btn-danger' : 'btn-primary'}`, text: opts.confirm, attrs: { type: 'button' }, on: { click: () => done(true) } }), cancel),
    );
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      done(false);
    });
    document.body.append(dialog);
    dialog.showModal();
    cancel.focus();
  });
}

export interface TrashHandlers {
  load(): Promise<TrashedPhotoDto[]>;
  thumb(id: string): Promise<Blob>;
  restore(p: TrashedPhotoDto): Promise<void>;
  purge(p: TrashedPhotoDto): Promise<void>;
  /** 照片是谁传的 */
  nameOf(memberId: string): string;
  onClose(): void;
}

const DAY_MS = 86_400_000;

/**
 * 回收站：放进来的照片按时间排，新的在前；每张能恢复、能永久删除（再问一次）。
 * 缩略图滚到跟前才取（带令牌，所以不能直接写 img 的地址）。开着时 Ta 那边删了、恢复了，main.ts 调 refresh 重新列。
 */
export function trashDialog(h: TrashHandlers): { refresh(): void } {
  const list = el('div', { class: 'trash-list' });
  const status = el('p', { class: 'trash-status', attrs: { role: 'status' } });
  const err = formError();
  const dialog = el('dialog', { class: 'dialog dialog-trash' });
  const urls: string[] = [];
  const pending = new Map<Element, string>();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const id = pending.get(e.target);
        if (!e.isIntersecting || !id) continue;
        pending.delete(e.target);
        observer.unobserve(e.target);
        const img = e.target.querySelector('img')!;
        h.thumb(id).then(
          (b) => {
            const url = URL.createObjectURL(b);
            urls.push(url);
            img.src = url;
            img.hidden = false;
          },
          () => undefined,
        );
      }
    },
    { root: list, rootMargin: '160px' },
  );
  let restored = 0, purged = 0;
  const tell = () => {
    status.textContent = [restored ? `已恢复 ${restored} 张` : '', purged ? `已永久删除 ${purged} 张` : ''].filter(Boolean).join('，');
  };
  const item = (p: TrashedPhotoDto) => {
    const { y, m, d } = parseLocalDate(p.date);
    const left = Math.ceil((Date.parse(p.purgeAt) - Date.now()) / DAY_MS);
    const img = el('img', { attrs: { alt: '', decoding: 'async' } });
    img.hidden = true;
    const thumb = el('div', { class: 'trash-thumb' }, img);
    thumb.style.background = p.color;
    const restore = el('button', { class: 'btn', text: '恢复', attrs: { type: 'button' } });
    const purge = el('button', { class: 'btn btn-ghost trash-purge', text: '永久删除', attrs: { type: 'button' } });
    const node = el(
      'figure',
      { class: 'trash-item' },
      thumb,
      el(
        'figcaption',
        {},
        el('span', { class: 'trash-date', text: `${y} 年 ${m} 月 ${d} 日` }),
        el('span', { class: 'trash-meta', text: `${left > 0 ? `还剩 ${left} 天` : '马上会永久删除'} · ${h.nameOf(p.createdBy)} 传的` }),
      ),
      el('div', { class: 'trash-actions' }, restore, purge),
    );
    const busy = (on: boolean) => (restore.disabled = purge.disabled = on);
    restore.addEventListener('click', async () => {
      busy(true);
      err.textContent = '';
      try {
        await h.restore(p);
        node.remove();
        restored++;
        tell();
        if (!list.querySelector('.trash-item')) empty();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : String(e);
        busy(false);
      }
    });
    purge.addEventListener('click', async () => {
      const ok = await confirmDialog({ title: '永久删除这张照片？', text: '原图和缩略图会从服务器上删掉，找不回来；Ta 的回收站里也不会再有。', confirm: '永久删除', danger: true });
      if (!ok) return;
      busy(true);
      err.textContent = '';
      try {
        await h.purge(p);
        node.remove();
        purged++;
        tell();
        if (!list.querySelector('.trash-item')) empty();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : String(e);
        busy(false);
      }
    });
    pending.set(thumb, p.id);
    observer.observe(thumb);
    return node;
  };
  const empty = () => list.replaceChildren(el('p', { class: 'trash-empty', text: '回收站是空的' }));
  const refresh = async () => {
    try {
      const photos = await h.load();
      pending.clear();
      observer.disconnect();
      if (!photos.length) return empty();
      list.replaceChildren(...photos.map(item));
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : String(e);
    }
  };
  const close = () => {
    observer.disconnect();
    for (const u of urls) URL.revokeObjectURL(u);
    dialog.close();
    dialog.remove();
    h.onClose();
  };
  dialog.append(
    el('h2', { class: 'dialog-title', text: '回收站' }),
    el('p', { class: 'card-text', text: `删了的照片在这里留 ${LIMITS.trashDays} 天，你们两个人都能恢复；满了天数会自动永久删除。` }),
    list,
    status,
    err,
    el('div', { class: 'dialog-actions' }, el('button', { class: 'btn', text: '关闭', attrs: { type: 'button' }, on: { click: close } })),
  );
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  list.append(el('p', { class: 'trash-empty', text: '正在打开回收站…' }));
  document.body.append(dialog);
  dialog.showModal();
  void refresh();
  return { refresh: () => void refresh() };
}

// ── 时间胶囊（ADR-0011）────────────────────────────────────────────

/** 几个选项里挑一个（撤回时间胶囊：转成普通照片 / 彻底删掉）；Esc、「取消」是 null */
export function choiceDialog<T>(opts: { title: string; text: string; choices: { label: string; value: T; danger?: boolean }[] }): Promise<T | null> {
  return new Promise((resolve) => {
    const dialog = el('dialog', { class: 'dialog dialog-confirm' });
    const done = (v: T | null) => {
      dialog.close();
      dialog.remove();
      resolve(v);
    };
    const cancel = el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' }, on: { click: () => done(null) } });
    dialog.append(
      el('h2', { class: 'dialog-title', text: opts.title }),
      el('p', { class: 'card-text', text: opts.text }),
      el(
        'div',
        { class: 'dialog-actions dialog-actions-wrap' },
        ...opts.choices.map((c) => el('button', { class: `btn ${c.danger ? 'btn-danger' : ''}`, text: c.label, attrs: { type: 'button' }, on: { click: () => done(c.value) } })),
        cancel,
      ),
    );
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      done(null);
    });
    document.body.append(dialog);
    dialog.showModal();
    cancel.focus();
  });
}

/** 胶囊面板要显示的东西（main.ts 按 CapsuleDto 和现在的时间算好） */
export interface CapsuleView {
  capsule: CapsuleDto;
  phase: CapsulePhase;
  /** 我是封存的人 */
  mine: boolean;
  creatorName: string;
  partnerName: string;
  /** 「还有 128 天」；到了是空字符串 */
  countdown: string;
  /** 里面那张照片的清晰图（能看里面、有照片时） */
  photoBlob?: () => Promise<Blob>;
  placeholder?: string;
}

export interface CapsuleHandlers {
  onClose(): void;
  /** 长按的进度：0–1 按着，-1 没按满就松手了（main.ts 管声音、震动、星茧变亮） */
  onPress(k: number): void;
  /** 按满了：拆开；失败时抛出的错误显示在面板里 */
  onOpen(): Promise<void>;
  onPeek(): Promise<void>;
  /** 改信（null 是不要信了） */
  onLetter(text: string | null): Promise<void>;
  onDate(date: LocalDate): Promise<void>;
  /** 撤回；keepPhoto 时里面的照片转成普通照片 */
  onWithdraw(keepPhoto: boolean): Promise<void>;
}

const HOLD_MS = 2000;

/**
 * 时间胶囊的面板：和照片面板一个位置，两个同时只开一个。
 * 看不到里面：一颗星茧、「请在 2027.02.14 再来敲门」和倒计时；到了是「长按 2 秒，打开它」（Ta 先拆了是重播）。
 * 看得到里面：照片和信；秘密惊喜里封存的人还有「星海保管者特权」的说明和改信、调日子、撤回。
 */
export function mountCapsulePanel(parent: HTMLElement, handlers: CapsuleHandlers, opts: { dust: Stardust; reduced: boolean }) {
  const kind = el('span', { class: 'capsule-kind' });
  const when = el('p', { class: 'capsule-when' });
  const byline = el('p', { class: 'capsule-by' });
  const orb = el('div', { class: 'capsule-orb', attrs: { 'aria-hidden': 'true' } }, el('span'));
  const line = el('p', { class: 'capsule-line' });
  const count = el('p', { class: 'capsule-count' });
  const privilege = el('p', { class: 'capsule-privilege' });
  const img = el('img', { attrs: { alt: '胶囊里的照片', decoding: 'async' } });
  const photo = el('figure', { class: 'capsule-photo' }, img);
  const letter = el('p', { class: 'capsule-letter' });
  const peek = el('p', { class: 'capsule-peek' });
  const status = el('p', { class: 'capsule-status' });
  const hold = el('button', { class: 'capsule-hold', attrs: { type: 'button' } });
  const err = el('p', { class: 'form-error capsule-error', attrs: { role: 'alert' } });
  const peekButton = el('button', { class: 'btn', text: '偷看一眼', attrs: { type: 'button' } });
  const letterButton = el('button', { class: 'btn btn-ghost', text: '改寄语', attrs: { type: 'button' } });
  const dateButton = el('button', { class: 'btn btn-ghost', text: '调整日期', attrs: { type: 'button' } });
  const withdrawButton = el('button', { class: 'btn btn-ghost', text: '撤回', attrs: { type: 'button' } });
  const actions = el('div', { class: 'capsule-actions' }, peekButton, letterButton, dateButton, withdrawButton);
  const slot = el('div', { class: 'capsule-slot' });
  const panel = el(
    'aside',
    { class: 'panel capsule-panel', attrs: { 'aria-label': '时间胶囊' } },
    el('button', { class: 'panel-close', text: '×', attrs: { type: 'button', 'aria-label': '关闭' }, on: { click: handlers.onClose } }),
    el('div', { class: 'capsule-head' }, kind, when, byline),
    orb,
    line,
    count,
    privilege,
    photo,
    letter,
    peek,
    status,
    hold,
    err,
    slot,
    actions,
  );
  panel.hidden = true;
  parent.append(panel);
  let view: CapsuleView | null = null;
  let url: string | null = null;
  let gen = 0;

  const setUrl = (next: string | null) => {
    if (url && url !== next) URL.revokeObjectURL(url);
    url = next;
  };

  /** 写在面板里的小表单（改信、调日子） */
  const openForm = (node: HTMLElement) => {
    slot.replaceChildren(node);
    actions.hidden = true;
  };
  const closeForm = () => {
    slot.replaceChildren();
    if (view) render(view);
  };
  const run = async (work: () => Promise<void>) => {
    err.textContent = '';
    try {
      await work();
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : String(e);
    }
  };

  letterButton.addEventListener('click', () => {
    const c = view?.capsule;
    if (!c) return;
    const area = el('textarea', { class: 'capsule-input', attrs: { rows: '5', maxlength: String(LIMITS.letterLength), placeholder: '写给 Ta 的信…' } });
    area.value = c.letter ?? '';
    const save = el('button', { class: 'btn btn-primary', text: '保存', attrs: { type: 'button' } });
    save.addEventListener('click', () => void run(async () => {
      await handlers.onLetter(area.value.trim() ? area.value : null);
      closeForm();
    }));
    openForm(el('div', { class: 'capsule-form' }, area, el('div', { class: 'card-editor-row' }, save, el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' }, on: { click: closeForm } }))));
    area.focus();
  });
  dateButton.addEventListener('click', () => {
    const c = view?.capsule;
    if (!c) return;
    const tomorrow = new Date(Date.now() + 86_400_000);
    const pick = input({ type: 'date', min: localDateOf(tomorrow), value: c.date });
    const save = el('button', { class: 'btn btn-primary', text: '保存', attrs: { type: 'button' } });
    save.addEventListener('click', () => void run(async () => {
      if (!pick.value) throw new Error('挑一个解封的日子');
      await handlers.onDate(pick.value as LocalDate);
      closeForm();
    }));
    openForm(el('div', { class: 'capsule-form' }, field('新的解封日子（那天零点）', pick), el('div', { class: 'card-editor-row' }, save, el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' }, on: { click: closeForm } }))));
  });
  peekButton.addEventListener('click', () => void run(async () => {
    const ok = await confirmDialog({ title: '偷看一眼？', text: '偷看会提前打破魔法哦，确定现在就要查看吗？星茧下面会留下一个小小的印记，Ta 也看得见。', confirm: '偷看' });
    if (ok) await handlers.onPeek();
  }));
  withdrawButton.addEventListener('click', () => void run(async () => {
    const c = view?.capsule;
    if (!c) return;
    const keep = c.hasPhoto
      ? await choiceDialog({
          title: '撤回这颗时间胶囊？',
          text: '撤回以后 Ta 那边的星茧也会消失。里面的照片可以转成普通照片，回到它拍摄的那天，两个人都看得到；也可以连照片一起删掉。信不会留下。',
          choices: [
            { label: '转成普通照片', value: true },
            { label: '彻底删掉', value: false, danger: true },
          ],
        })
      : (await confirmDialog({ title: '撤回这颗时间胶囊？', text: '撤回以后 Ta 那边的星茧也会消失，信不会留下。', confirm: '撤回', danger: true }))
        ? false
        : null;
    if (keep !== null) await handlers.onWithdraw(keep);
  }));

  // ── 长按 2 秒拆开 ──
  let pressing: { t0: number; raf: number } | null = null;
  const stopPress = (cancelled: boolean) => {
    if (!pressing) return;
    cancelAnimationFrame(pressing.raf);
    pressing = null;
    hold.style.setProperty('--p', '0');
    hold.classList.remove('capsule-holding');
    if (cancelled) handlers.onPress(-1);
  };
  const tick = () => {
    if (!pressing) return;
    const k = Math.min(1, (performance.now() - pressing.t0) / HOLD_MS);
    hold.style.setProperty('--p', k.toFixed(3));
    handlers.onPress(k);
    if (k < 1) {
      pressing.raf = requestAnimationFrame(tick);
      return;
    }
    stopPress(false);
    hold.disabled = true;
    void run(handlers.onOpen).finally(() => (hold.disabled = false));
  };
  const startPress = () => {
    if (pressing || hold.disabled) return;
    err.textContent = '';
    hold.classList.add('capsule-holding');
    pressing = { t0: performance.now(), raf: requestAnimationFrame(tick) };
  };
  hold.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    // 手指滑出按钮也算按着；拿不到指针（比如浏览器不认这个指针）时照样开始
    try {
      hold.setPointerCapture(e.pointerId);
    } catch {
      // 没捕获到也不影响长按
    }
    startPress();
  });
  for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) hold.addEventListener(type, () => stopPress(true));
  hold.addEventListener('contextmenu', (e) => e.preventDefault());
  // 键盘：按住空格或回车
  hold.addEventListener('keydown', (e) => {
    if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
      e.preventDefault();
      startPress();
    }
  });
  hold.addEventListener('keyup', (e) => {
    if (e.key === ' ' || e.key === 'Enter') stopPress(true);
  });

  const render = (v: CapsuleView) => {
    const c = v.capsule, inside = c.letter !== undefined || c.photo !== undefined;
    const partnerOpened = c.openedBy.some((id) => id !== (v.mine ? c.createdBy : ''));
    kind.textContent = c.kind === 'surprise' ? '秘密惊喜' : '共同契约';
    kind.dataset.kind = c.kind;
    const { y, m, d } = parseLocalDate(c.date);
    when.textContent = `${y} 年 ${m} 月 ${d} 日解封`;
    const made = new Date(c.createdAt);
    byline.textContent = `${v.creatorName} 封存于 ${made.getFullYear()}.${String(made.getMonth() + 1).padStart(2, '0')}.${String(made.getDate()).padStart(2, '0')}`;
    orb.hidden = inside;
    orb.dataset.phase = v.phase;
    // 主要的那句话
    let say = '';
    if (!inside && v.phase !== 'ready') say = v.mine && c.kind === 'pact' ? '这是你们约好的星，连你自己也先封起来了' : `这是一颗未来的星，请在 ${c.date.replaceAll('-', '.')} 再来敲门`;
    else if (!inside && v.phase === 'ready') say = partnerOpened ? `${v.partnerName} 已经为你开启了一颗时间胶囊` : '时间到了';
    line.textContent = say;
    line.hidden = !say;
    count.textContent = !inside && v.countdown ? (v.mine && c.kind === 'pact' ? `距离兑现诺言${v.countdown}` : v.countdown) + (v.phase === 'warming' ? ' · 裂缝已经在发光了' : '') : '';
    count.hidden = !count.textContent;
    privilege.textContent =
      inside && v.mine && c.kind === 'surprise' && !c.openedBy.some((id) => id !== c.createdBy) ? `✦ 星海保管者特权：这是你为 ${v.partnerName} 准备的秘密星茧，当前仅对你可见。解封前 Ta 看不到任何内容。` : '';
    privilege.hidden = !privilege.textContent;
    photo.hidden = !(inside && c.photo);
    if (inside && c.photo) {
      const ar = c.photo.width && c.photo.height ? c.photo.width / c.photo.height : 4 / 3;
      photo.style.aspectRatio = String(Math.min(1.8, Math.max(0.56, ar)));
    }
    letter.textContent = c.letter ?? '';
    letter.hidden = !(inside && c.letter);
    peek.textContent = c.peekCount && c.peekedAt ? peekMarkText(c.peekCount, c.peekedAt) : '';
    peek.hidden = !peek.textContent;
    // 长按拆开：到了、你还没拆；秘密惊喜的封存的人本来就看得到，不用拆
    const canOpen = v.phase === 'ready' && !(v.mine && c.kind === 'surprise');
    // 封存的人看到的状态：Ta 拆没拆（等着你拆的时候上面那句已经说了，这里不重复）
    const others = c.openedBy.filter((id) => id !== c.createdBy);
    status.textContent = canOpen ? '' : v.mine ? (others.length ? `${v.partnerName} 已经亲手拆开了` : v.phase === 'ready' || v.phase === 'opened' ? `时间到了，等 ${v.partnerName} 来拆开` : '') : v.phase === 'opened' ? '你已经亲手拆开了' : '';
    status.hidden = !status.textContent;
    hold.hidden = !canOpen;
    hold.textContent = partnerOpened ? '长按 2 秒，看 Ta 为你打开的那一刻' : '长按 2 秒，打开它';
    // 封存的人能做的事
    const before = v.phase === 'sealed' || v.phase === 'warming';
    peekButton.hidden = !(v.mine && c.kind === 'pact' && !inside && before);
    letterButton.hidden = dateButton.hidden = !(v.mine && c.kind === 'surprise' && before && c.openedBy.length === 0);
    withdrawButton.hidden = !v.mine;
    actions.hidden = slot.childElementCount > 0 || (peekButton.hidden && letterButton.hidden && withdrawButton.hidden);
  };

  /** 照片：先占位图，清晰图到了再换；reveal 时星尘从星茧飞进画框凝聚成照片 */
  const loadPhoto = (v: CapsuleView, reveal: (() => Point | null) | null) => {
    const mine = ++gen, p = v.capsule.photo;
    setUrl(null);
    img.removeAttribute('src');
    if (!p) return;
    if (v.placeholder) img.src = v.placeholder;
    const accent = p.color;
    if (reveal && !opts.reduced) {
      img.style.opacity = '0';
      const ok = opts.dust.open({
        image: swatchCanvas(accent),
        accent,
        radius: 14,
        from: reveal,
        frame: () => photo.getBoundingClientRect(),
        onDone: () => mine === gen && (img.style.opacity = ''),
      });
      if (!ok) img.style.opacity = '';
    } else img.style.opacity = '';
    v.photoBlob?.()
      .then((b) => {
        if (mine !== gen) return;
        const next = URL.createObjectURL(b);
        setUrl(next);
        img.src = next;
        img.decode().then(
          () => mine === gen && opts.dust.running && opts.dust.setImage(img),
          () => undefined,
        );
      })
      .catch(() => undefined);
  };

  return {
    /** reveal：刚拆开，星尘从星茧（这个位置）飞进来凝聚成照片，信慢慢浮现 */
    show(v: CapsuleView, how: { reveal?: () => Point | null } = {}) {
      const same = view?.capsule.id === v.capsule.id, hadPhoto = view?.capsule.photo?.id;
      view = v;
      if (!same) {
        slot.replaceChildren();
        err.textContent = '';
        stopPress(true);
      }
      panel.hidden = false;
      panel.classList.toggle('capsule-revealing', Boolean(how.reveal));
      render(v);
      if (!same || how.reveal || hadPhoto !== v.capsule.photo?.id) loadPhoto(v, how.reveal ?? null);
    },
    hide() {
      if (panel.hidden) return;
      stopPress(true);
      gen++;
      setUrl(null);
      img.removeAttribute('src');
      slot.replaceChildren();
      view = null;
      panel.hidden = true;
    },
    get open(): boolean {
      return !panel.hidden;
    },
    /** 正显示的那颗胶囊 */
    get id(): string | null {
      return view?.capsule.id ?? null;
    },
  };
}

/** 「已于 2026.10 被创建者偷偷翻阅过 1 次」（同 capsule.ts 的 peekMark） */
function peekMarkText(n: number, at: string): string {
  const d = new Date(at);
  return `已于 ${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')} 被创建者偷偷翻阅过 ${n} 次`;
}

export interface SealValues {
  kind: CapsuleKind;
  letter: string;
  file: File | null;
  date: LocalDate;
}

/**
 * 封存一颗时间胶囊：选形态（秘密惊喜 / 共同契约）、写信、可选一张照片、挑解封的日子（现成的纪念日，或者自己挑）。
 * 解封是那天的零点（按这台设备的时区）。
 */
export function capsuleDialog(opts: { presets: { label: string; date: LocalDate }[]; partnerName: string; save(v: SealValues): Promise<void> }): void {
  const kinds = (['surprise', 'pact'] as const).map((k) => {
    const radio = input({ type: 'radio', name: 'capsuleKind', value: k });
    if (k === 'surprise') radio.checked = true;
    return el(
      'label',
      { class: 'capsule-choice' },
      radio,
      el('span', { class: 'capsule-choice-title', text: k === 'surprise' ? '秘密惊喜' : '共同契约' }),
      el('span', {
        class: 'capsule-choice-text',
        text: k === 'surprise' ? `只有你看得到，到日子给 ${opts.partnerName} 一个惊喜。你随时能看、能改、能撤回。` : '写给未来的你们，连你自己也先封起来。忍不住可以偷看，但会留下印记。',
      }),
    );
  });
  const words = el('textarea', { attrs: { name: 'letter', rows: '5', maxlength: String(LIMITS.letterLength), placeholder: '写一封信…' } });
  const file = input({ type: 'file', accept: 'image/*,.heic,.heif', hidden: '' });
  const preview = el('img', { class: 'capsule-preview', attrs: { alt: '' } });
  preview.hidden = true;
  const fileName = el('span', { class: 'field-hint', text: '可以不放照片' });
  const removeFile = el('button', { class: 'btn btn-ghost', text: '不放了', attrs: { type: 'button' } });
  removeFile.hidden = true;
  let chosen: File | null = null;
  let previewUrl: string | null = null;
  const setFile = (f: File | null) => {
    chosen = f;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = f ? URL.createObjectURL(f) : null;
    preview.src = previewUrl ?? '';
    preview.hidden = !f || /\.hei[cf]$/i.test(f.name);
    fileName.textContent = f ? f.name : '可以不放照片';
    removeFile.hidden = !f;
  };
  file.addEventListener('change', () => setFile(file.files?.[0] ?? null));
  removeFile.addEventListener('click', () => {
    file.value = '';
    setFile(null);
  });
  const tomorrow = localDateOf(new Date(Date.now() + 86_400_000));
  const pick = input({ type: 'date', min: tomorrow, required: '' });
  const summary = el('p', { class: 'field-hint' });
  const chips = opts.presets.map((p) =>
    el('button', {
      class: 'chip',
      text: p.label,
      attrs: { type: 'button', title: p.date },
      on: {
        click: () => {
          pick.value = p.date;
          sync();
        },
      },
    }),
  );
  const sync = () => {
    for (const [k, p] of opts.presets.entries()) chips[k]!.classList.toggle('chip-on', pick.value === p.date);
    if (!pick.value) return void (summary.textContent = '解封是那天的零点。');
    const { y, m, d } = parseLocalDate(pick.value as LocalDate);
    const days = Math.round((new Date(y, m - 1, d).getTime() - new Date(new Date().toDateString()).getTime()) / 86_400_000);
    summary.textContent = `${y} 年 ${m} 月 ${d} 日零点解封${days > 0 ? `，还有 ${days.toLocaleString('zh-CN')} 天` : ''}`;
  };
  pick.addEventListener('input', sync);
  sync();
  const err = formError();
  const dialog = el('dialog', { class: 'dialog' });
  const close = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    dialog.close();
    dialog.remove();
  };
  const form = el(
    'form',
    { class: 'form' },
    el('h2', { class: 'dialog-title', text: '封存一颗时间胶囊' }),
    el('div', { class: 'capsule-kinds', attrs: { role: 'radiogroup', 'aria-label': '形态' } }, ...kinds),
    field('信', words),
    el('div', { class: 'field' }, el('span', { class: 'field-label', text: '照片' }), el('div', { class: 'capsule-file-row' }, el('label', { class: 'btn', text: '选一张…' }, file), fileName, removeFile), preview),
    el('div', { class: 'field' }, el('span', { class: 'field-label', text: '哪天解封' }), el('div', { class: 'chips' }, ...chips), pick, summary),
    err,
    el(
      'div',
      { class: 'dialog-actions' },
      el('button', { class: 'btn btn-primary', text: '封存', attrs: { type: 'submit' } }),
      el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' }, on: { click: close } }),
    ),
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void submitting(form, err, async () => {
      const kind = (form.querySelector<HTMLInputElement>('input[name=capsuleKind]:checked')?.value ?? 'surprise') as CapsuleKind;
      if (!words.value.trim() && !chosen) throw new Error('写一封信，或者放一张照片');
      if (!pick.value) throw new Error('挑一个解封的日子');
      if (pick.value < tomorrow) throw new Error('解封的日子要在明天以后');
      await opts.save({ kind, letter: words.value, file: chosen, date: pick.value as LocalDate });
      close();
    });
  });
  dialog.append(form);
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  document.body.append(dialog);
  dialog.showModal();
}

// ── 入场暗号（ADR-0011，design §11.8）────────────────────────────

/** 「10 月 3 日」 */
function monthDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日`;
}

export interface NoteDialogOptions {
  /** Ta 的称呼；Ta 还没加入时是 null（留不了） */
  partnerName: string | null;
  /** 我留的、Ta 还没看到的那句：再写就是改它 */
  pending: NoteDto | null;
  /** 我留的、Ta 最近看到的那句 */
  lastRead: NoteDto | null;
  save(text: string): Promise<void>;
  withdraw(): Promise<void>;
}

/**
 * 给 Ta 留一句入场暗号：Ta 下次打开星海时，入场的那段话换成这一句，下面一行小字写着是你留的；
 * Ta 看到以后，它是今天的一个足迹。Ta 还没看到时再写就是改这一句，也能撤回。
 */
export function noteDialog(o: NoteDialogOptions): void {
  const partner = o.partnerName ?? 'Ta';
  const area = el('textarea', { attrs: { name: 'note', rows: '4', maxlength: String(LIMITS.noteLength), placeholder: '比如：今晚的月亮很圆，想和你一起看' } });
  area.value = o.pending?.text ?? '';
  const count = el('span', { class: 'field-hint note-count' });
  const recount = () => (count.textContent = `${[...area.value.trim()].length} / ${LIMITS.noteLength}`);
  area.addEventListener('input', recount);
  recount();
  const status = el('p', { class: 'field-hint note-status' });
  status.textContent = !o.partnerName
    ? 'Ta 还没加入星海：先点「邀请」把邀请码发给 Ta。'
    : o.pending
      ? `${partner} 还没看到这一句（${monthDay(o.pending.updatedAt)}留的）。现在改，Ta 看到的就是改过的。`
      : o.lastRead
        ? `上一句 ${partner} ${monthDay(o.lastRead.readAt!)}看到了，成了那天的一个足迹。`
        : '';
  status.hidden = !status.textContent;
  const err = formError();
  const dialog = el('dialog', { class: 'dialog' });
  const close = () => {
    dialog.close();
    dialog.remove();
  };
  const save = el('button', { class: 'btn btn-primary', text: o.pending ? '改好了' : '留下', attrs: { type: 'submit' } });
  save.disabled = !o.partnerName;
  const withdraw = el('button', { class: 'btn btn-ghost', text: '撤回这一句', attrs: { type: 'button' } });
  withdraw.hidden = !o.pending;
  const form = el(
    'form',
    { class: 'form' },
    el('h2', { class: 'dialog-title', text: `给 ${partner} 留一句入场暗号` }),
    el('p', { class: 'card-text', text: `下次 ${partner} 打开星海时，入场的那段话会换成这一句，下面写着是你留的。${partner} 看到以后，它会成为今天的一个小足迹。` }),
    field('想对 Ta 说的话', area),
    count,
    status,
    err,
    el('div', { class: 'dialog-actions' }, save, withdraw, el('button', { class: 'btn btn-ghost', text: '取消', attrs: { type: 'button' }, on: { click: close } })),
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void submitting(form, err, async () => {
      const text = area.value.trim();
      if (!text) throw new Error(o.pending ? '想撤回的话点「撤回这一句」' : '写一句想对 Ta 说的话');
      if (text.split('\n').length > LIMITS.noteLines) throw new Error(`最多 ${LIMITS.noteLines} 行`);
      await o.save(text);
      close();
    });
  });
  withdraw.addEventListener('click', () => {
    void submitting(form, err, async () => {
      await o.withdraw();
      close();
    });
  });
  // ⌘ / Ctrl + 回车留下（和写故事一样）
  area.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.isComposing) {
      e.preventDefault();
      form.requestSubmit();
    }
  });
  dialog.append(form);
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  document.body.append(dialog);
  dialog.showModal();
  area.focus();
}

/** 足迹：Ta 看过的入场暗号（点螺线上那颗小星打开），只能看 */
export function footprintDialog(o: { text: string; fromName: string; toName: string; left: string; read: string }): void {
  const dialog = el('dialog', { class: 'dialog dialog-note' });
  const close = () => {
    dialog.close();
    dialog.remove();
  };
  const done = el('button', { class: 'btn btn-ghost', text: '关上', attrs: { type: 'button' }, on: { click: close } });
  dialog.append(
    el('p', { class: 'note-kicker', text: `入场暗号 · ${o.fromName} 留给 ${o.toName}` }),
    el('p', { class: 'note-text', text: o.text }),
    el('p', { class: 'note-meta', text: `${o.left}留 · ${o.read}看到` }),
    el('div', { class: 'dialog-actions' }, done),
  );
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  // 点对话框外面（背景）也关上
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) close();
  });
  document.body.append(dialog);
  dialog.showModal();
  done.focus();
}

